import { AI_CREDITS_DISPLAY_MULTIPLIER, FREE_LIMITS, EXPIRED_LIMITS, LIMIT_KEY_BY_METRIC } from '@superfunnel/types/usage-limits';

/**
 * Limit Resolution Logic
 *
 * Resolves effective limits for an account based on:
 * 1. Account-level overrides (account.limits)
 * 2. Custom plan limits (subscription.customLimits)
 * 3. Plan tier defaults (from plan-config)
 * 4. Expired limits (when subscription is expired)
 *
 * Limit value meanings:
 * - null = unlimited
 * - undefined = use plan tier default
 * - number = specific limit
 *
 * This module is shared between backend (functions-utils) and frontend (dashboard).
 */
/**
 * Grace period duration in days after subscription expires.
 * During grace period, accounts get individual plan limits.
 */
const GRACE_PERIOD_DAYS = 7;
/**
 * Default plan limits by tier.
 * These are the baseline limits when no overrides are set.
 *
 * Note: aiCredits values are in credit units (1 credit = $0.01).
 */
const DEFAULT_TIER_LIMITS = {
    'individual': {
        domains: 1,
        pages: null, // Unlimited for now
        publishedPages: 1,
        quizzes: null, // Unlimited for now, mirroring pages
        publishedQuizzes: 1,
        members: 1,
        aiCredits: 10 * AI_CREDITS_DISPLAY_MULTIPLIER,
        sessions: 1000,
    },
    'starter-5k': {
        domains: 3,
        pages: null, // Unlimited for now
        publishedPages: 3,
        quizzes: null, // Unlimited for now, mirroring pages
        publishedQuizzes: 3,
        members: 3,
        aiCredits: 50 * AI_CREDITS_DISPLAY_MULTIPLIER,
        sessions: 5000,
    },
    'starter-10k': {
        domains: 5,
        pages: null, // Unlimited for now
        publishedPages: 5,
        quizzes: null, // Unlimited for now, mirroring pages
        publishedQuizzes: 5,
        members: 3,
        aiCredits: 100 * AI_CREDITS_DISPLAY_MULTIPLIER,
        sessions: 10000,
    },
    'scale-15k': {
        domains: 5,
        pages: null, // Unlimited for now
        publishedPages: 10,
        quizzes: null, // Unlimited for now, mirroring pages
        publishedQuizzes: 10,
        members: 5,
        aiCredits: 200 * AI_CREDITS_DISPLAY_MULTIPLIER,
        sessions: 15000,
    },
    'scale-25k': {
        domains: 7,
        pages: null, // Unlimited for now
        publishedPages: 15,
        quizzes: null, // Unlimited for now, mirroring pages
        publishedQuizzes: 15,
        members: 5,
        aiCredits: 300 * AI_CREDITS_DISPLAY_MULTIPLIER,
        sessions: 25000,
    },
    'scale-50k': {
        domains: 10,
        pages: null, // Unlimited for now
        publishedPages: 25,
        quizzes: null, // Unlimited for now, mirroring pages
        publishedQuizzes: 25,
        members: 5,
        aiCredits: 500 * AI_CREDITS_DISPLAY_MULTIPLIER,
        sessions: 50000,
    },
    'custom': {
        // Custom plans use customLimits from subscription
        // Falls back to individual tier limits if customLimits not configured
        // This includes Lemon Squeezy products with unknown variant IDs
        domains: 1,
        pages: null,
        publishedPages: 1,
        // Quiz limits post-date custom plans, so no existing contract can have
        // negotiated one. Staying unlimited until an admin sets customLimits keeps
        // this fallback from retroactively capping live custom accounts.
        quizzes: null,
        publishedQuizzes: null,
        members: 1,
        aiCredits: 10 * AI_CREDITS_DISPLAY_MULTIPLIER,
        // Post-dates custom plans too, so it stays unlimited until set, as quizzes do.
        sessions: null,
    },
};
/**
 * User-friendly messages for each metric.
 */
const LIMIT_MESSAGES = {
    domains: 'You have reached your custom domain limit. Please upgrade your plan or remove existing domains.',
    pages: 'You have reached your page limit. Please upgrade your plan or delete existing pages.',
    publishedPages: 'You have reached your published page limit. Please upgrade your plan or unpublish existing pages.',
    quizzes: 'You have reached your quiz limit. Please upgrade your plan or delete existing quizzes.',
    publishedQuizzes: 'You have reached your published quiz limit. Please upgrade your plan or unpublish existing quizzes.',
    members: 'You have reached your team member limit. Please upgrade your plan or remove existing members.',
    aiTokens: 'You have reached your AI spending limit. Please upgrade your plan or purchase additional credits.',
};
/**
 * Map from UsageMetricType to PlanLimits key.
 *
 * Casting the metric instead would compile, because `as` silences the mismatch,
 * and then every `aiTokens` lookup would miss: its limit is stored under
 * `aiCredits`, so the resolver fell through to "unlimited" on paid plans and to
 * the `?? 0` fallback on free ones.
 */
function getLimitKeyForMetric(metric) {
    return LIMIT_KEY_BY_METRIC[metric];
}
/**
 * Convert a Timestamp-like value to a Date.
 * Handles both Firebase Admin SDK Timestamp (with .toDate()) and serialized
 * Firestore timestamps (with _seconds).
 */
function toDate(value) {
    if (value instanceof Date)
        return value;
    if (value != null &&
        typeof value === 'object' &&
        'toDate' in value &&
        typeof value.toDate === 'function') {
        return value.toDate();
    }
    if (value != null &&
        typeof value === 'object' &&
        '_seconds' in value &&
        typeof value._seconds === 'number') {
        return new Date(value._seconds * 1000);
    }
    return new Date(value);
}
/**
 * Determine the current plan state for an account.
 *
 * @param account - The account document
 * @param subscription - The subscription document (optional)
 * @returns The account's plan state
 */
function getAccountPlanState(account, subscription) {
    if (!subscription) {
        // No subscription - check if account ever had one
        const lastSubscriptionEnd = account.billing?.currentPeriodEnd;
        if (lastSubscriptionEnd) {
            // Had a subscription before - check grace period
            const endDate = toDate(lastSubscriptionEnd);
            const gracePeriodEnd = new Date(endDate);
            gracePeriodEnd.setDate(gracePeriodEnd.getDate() + GRACE_PERIOD_DAYS);
            if (new Date() < gracePeriodEnd) {
                return 'grace';
            }
            return 'expired';
        }
        // Never had a subscription → free account
        return 'free';
    }
    const status = subscription.status;
    switch (status) {
        case 'active':
            return 'active';
        case 'on_trial':
            return 'trial';
        case 'cancelled':
            // Cancelled but still within paid period
            if (subscription.endsAt) {
                const endsAt = toDate(subscription.endsAt);
                if (new Date() < endsAt) {
                    return 'active';
                }
            }
            return 'expired';
        case 'expired':
        case 'unpaid':
        case 'past_due':
            return 'expired';
        case 'paused':
            return 'grace'; // Paused gets grace period limits
        default:
            return 'expired';
    }
}
/**
 * Get the effective limit for a specific metric.
 *
 * Resolution order:
 * 1. Account-level override (account.limits[metric])
 * 2. For free state: return FREE_LIMITS
 * 3. For expired state: return EXPIRED_LIMITS
 * 4. For active/trial: subscription.customLimits[metric] (custom plans)
 * 5. For active/trial: plan tier default
 * 6. For grace: individual plan limits
 * 7. No limit defined = null (unlimited)
 *
 * @param metric - The metric to get the limit for
 * @param account - The account document
 * @param subscription - The subscription document (optional)
 * @returns The effective limit (null = unlimited)
 */
function getEffectiveLimit(metric, account, subscription) {
    return getEffectiveLimitForKey(getLimitKeyForMetric(metric), account, subscription);
}
/**
 * Resolves a limit by its `PlanLimits` key, in the same order as
 * `getEffectiveLimit`. For limits that have no usage metric, like sessions.
 */
function getEffectiveLimitForKey(limitKey, account, subscription) {
    // 1. Check account-level override
    const accountLimit = account.limits?.[limitKey];
    if (accountLimit !== undefined) {
        // null = explicitly unlimited
        if (accountLimit === null)
            return null;
        return accountLimit;
    }
    // 2. Determine account state
    const state = getAccountPlanState(account, subscription);
    if (state === 'free') {
        return FREE_LIMITS[limitKey] ?? 0;
    }
    if (state === 'expired') {
        // Return expired limits (all zeros)
        return EXPIRED_LIMITS[limitKey] ?? 0;
    }
    // 3. For active/trial, check custom limits first (for custom plans)
    if (state === 'active' || state === 'trial') {
        if (subscription?.customLimits) {
            const customLimit = subscription.customLimits[limitKey];
            if (customLimit !== undefined) {
                if (customLimit === null)
                    return null;
                return customLimit;
            }
        }
        // 4. Get from plan tier defaults
        if (subscription?.planType) {
            const tierLimits = DEFAULT_TIER_LIMITS[subscription.planType];
            const tierLimit = tierLimits?.[limitKey];
            if (tierLimit !== undefined) {
                if (tierLimit === null)
                    return null;
                return tierLimit;
            }
        }
    }
    // 5. Grace period - use individual plan limits
    if (state === 'grace') {
        const individualLimits = DEFAULT_TIER_LIMITS['individual'];
        const tierLimit = individualLimits?.[limitKey];
        if (tierLimit !== undefined) {
            if (tierLimit === null)
                return null;
            return tierLimit;
        }
    }
    // 6. No limit defined = unlimited
    return null;
}
/**
 * Get all effective limits for an account.
 *
 * @param account - The account document
 * @param subscription - The subscription document (optional)
 * @returns All effective limits
 */
function getEffectiveLimits(account, subscription) {
    const metrics = [
        'domains',
        'pages',
        'publishedPages',
        'quizzes',
        'publishedQuizzes',
        'members',
        'aiTokens',
    ];
    const limits = {};
    for (const metric of metrics) {
        const value = getEffectiveLimit(metric, account, subscription);
        if (value !== null) {
            limits[getLimitKeyForMetric(metric)] = value;
        }
        // null means unlimited, so we don't include it in the result
    }
    const sessions = getEffectiveLimitForKey('sessions', account, subscription);
    if (sessions !== null) {
        limits.sessions = sessions;
    }
    // Buffer defaults to 0
    limits.buffer = account.limits?.buffer ?? 0;
    return limits;
}
/**
 * Check if an account has an unlimited limit for a metric.
 *
 * @param metric - The metric to check
 * @param account - The account document
 * @param subscription - The subscription document (optional)
 * @returns True if the limit is null (unlimited)
 */
function isUnlimited(metric, account, subscription) {
    return getEffectiveLimit(metric, account, subscription) === null;
}

export { DEFAULT_TIER_LIMITS, GRACE_PERIOD_DAYS, LIMIT_MESSAGES, getAccountPlanState, getEffectiveLimit, getEffectiveLimitForKey, getEffectiveLimits, getLimitKeyForMetric, isUnlimited };
//# sourceMappingURL=limit-resolution.esm.js.map
